import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import AnalyticsHome from './AnalyticsHome';
import DistanceAnalytics from './DistanceAnalytics';
import EquipmentAnalytics from './EquipmentAnalytics';
import WeightAnalytics from './WeightAnalytics';
import SpeedAnalytics from './SpeedAnalytics';
import RatingFormAnalytics from './RatingFormAnalytics';
import GateNumberAnalytics from './GateNumberAnalytics';
import TrainingSessionsAnalytics from './TrainingSessionsAnalytics';
import CompetitionSessionsAnalytics from './CompetitionSessionsAnalytics';
import GroundTrainingAnalytics from './GroundTrainingAnalytics';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
} from './styles';

const TabsContainer = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid var(--card-border);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  
  @media (max-width: 768px) {
    gap: 0.2rem;
    margin-bottom: 0;
    border-bottom-width: 1px;
  }
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--card-border);
    border-radius: 2px;
  }
`;


const TabButton = styled.button<{ $isActive: boolean }>`
  padding: 0.35rem 0.5rem;
  background: none;
  border: none;
  border-bottom: ${props => props.$isActive ? '3px solid var(--accent-orange)' : '3px solid transparent'};
  color: ${props => props.$isActive ? 'var(--accent-orange)' : 'var(--text-gray)'};
  font-family: 'Poppins', sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: -0.02em;
  
  &:hover {
    color: var(--accent-orange);
  }
  
  @media (max-width: 768px) {
    padding: var(--mobile-tab-padding, 0.3rem 0.45rem);
    font-size: var(--mobile-tab-font, 0.6rem);
  }
`;

const TabContent = styled.div`
  width: 100%;
`;

type TabType = 
  | 'general'
  | 'distance'
  | 'equipment'
  | 'weight'
  | 'inrun-speed'
  | 'rating-form'
  | 'gate-number'
  | 'training-sessions'
  | 'competition-sessions'
  | 'ground-training';

interface AnalyticsTabbedProps {
  isJumperMode?: boolean;
}

const AnalyticsTabbed: React.FC<AnalyticsTabbedProps> = ({ isJumperMode = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  
  // Get active tab from URL, default to 'general'
  const activeTab = (searchParams.get('tab') || 'general') as TabType;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Tab definitions
  const tabs: Array<{ id: TabType; label: string }> = [
    { id: 'general', label: 'General' },
    { id: 'distance', label: 'Distance' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'weight', label: 'Weight/Height' },
    { id: 'inrun-speed', label: 'Inrun Speed' },
    { id: 'rating-form', label: 'Rating / Form' },
    { id: 'gate-number', label: 'Gate Number' },
    { id: 'training-sessions', label: 'Training Sessions' },
    { id: 'competition-sessions', label: 'Competition Sessions' },
    { id: 'ground-training', label: 'Ground Training' },
  ];
  
  // Check if tabs container can scroll horizontally
  useEffect(() => {
    if (!isMobile || !tabsContainerRef.current) return;
    
    const checkScroll = () => {
      const container = tabsContainerRef.current;
      if (container) {
        const canScroll = container.scrollWidth > container.clientWidth;
        const isScrolledToEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
        setShowScrollIndicator(canScroll && !isScrolledToEnd);
      }
    };
    
    checkScroll();
    const container = tabsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [isMobile]);
  
  const handleTabChange = (tabId: TabType) => {
    setSearchParams({ tab: tabId });
  };
  
  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <AnalyticsHome isJumperMode={isJumperMode} />;
      case 'distance':
        return <DistanceAnalytics isJumperMode={isJumperMode} />;
      case 'equipment':
        return <EquipmentAnalytics isJumperMode={isJumperMode} />;
      case 'weight':
        return <WeightAnalytics isJumperMode={isJumperMode} />;
      case 'inrun-speed':
        return <SpeedAnalytics isJumperMode={isJumperMode} />;
      case 'rating-form':
        return <RatingFormAnalytics isJumperMode={isJumperMode} />;
      case 'gate-number':
        return <GateNumberAnalytics isJumperMode={isJumperMode} />;
      case 'training-sessions':
        return <TrainingSessionsAnalytics isJumperMode={isJumperMode} />;
      case 'competition-sessions':
        return <CompetitionSessionsAnalytics isJumperMode={isJumperMode} />;
      case 'ground-training':
        return <GroundTrainingAnalytics isJumperMode={isJumperMode} />;
      default:
        return <AnalyticsHome isJumperMode={isJumperMode} />;
    }
  };
  
  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Analytics Dashboard</PageTitle>
        <PageDescription>
          Overview of your training data, performance metrics, and key insights across all analytics categories.
        </PageDescription>
      </PageHeader>
      
      <TabsContainer ref={tabsContainerRef}>
        {tabs.map(tab => (
          <TabButton
            key={tab.id}
            $isActive={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabsContainer>
      
      {/* Scroll indicator text - below tabs */}
      {isMobile && showScrollIndicator && (
        <div style={{
          fontSize: '0.65rem',
          color: '#000',
          fontFamily: "'Poppins', sans-serif",
          marginTop: '-2rem',
          marginBottom: '0.75rem',
          fontWeight: 400,
          textAlign: 'left'
        }}>
          Scroll sideways to see all tags
        </div>
      )}
      
      <TabContent>
        {renderTabContent()}
      </TabContent>
    </AnalyticsContainer>
  );
};

export default AnalyticsTabbed;

